import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";

const UPDATED = "June 1, 2026";

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="font-display text-xl font-semibold text-white">{title}</h2>
    <div className="mt-3 space-y-3 text-sm leading-relaxed text-gray-400">{children}</div>
  </section>
);

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#0B0F17] text-gray-200" data-testid="privacy-policy-page">
      <header className="border-b border-white/10">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" data-testid="privacy-home-link" className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
          <div className="flex items-center gap-2 text-sm text-gray-300">
            <img src="/streamworship-mark.png" alt="StreamWorship" className="h-6 w-6 object-contain" /> StreamWorship
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-500/15 border border-amber-500/30">
            <ShieldCheck className="h-6 w-6 text-amber-400" />
          </span>
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-white">Privacy Policy</h1>
            <p className="text-xs text-gray-500 mt-1">Last updated: {UPDATED}</p>
          </div>
        </div>

        <p className="mt-8 text-sm leading-relaxed text-gray-400">
          StreamWorship ("StreamWorship", "we", "us", or "our") provides a live streaming and video
          hosting platform for churches and organizations, together with the <span className="text-gray-300">Ascend TV App</span> —
          a companion television and mobile application that lets viewers browse church channels and watch
          their live streams and video libraries. This Privacy Policy explains what information we collect
          across the StreamWorship platform and the Ascend TV App, how we use it, and the choices you have.
          By creating an account or using the service, you agree to the practices described here.
        </p>

        <Section title="Information we collect">
          <p>We collect information you provide directly and information generated as you use the service:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><span className="text-gray-300">Account details</span> — your name, email address, organization name, phone number, and password (stored only as a secure hash).</li>
            <li><span className="text-gray-300">Content you upload</span> — video files, recorded broadcasts, thumbnails, logos, and playlist information you create or stream through the platform.</li>
            <li><span className="text-gray-300">Streaming data</span> — stream keys, ingest activity, and live/on-air status used to operate your broadcasts.</li>
            <li><span className="text-gray-300">Billing information</span> — plan selection and subscription status. Card payments are processed by our payment provider; we do not store full card numbers on our servers.</li>
            <li><span className="text-gray-300">Usage &amp; device data</span> — log data, approximate location, and analytics about how streams and videos are viewed.</li>
          </ul>
        </Section>

        <Section title="How we use your information">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Provide, operate, and maintain your account and streaming/video features.</li>
            <li>Process subscriptions and send account, billing, and service notifications.</li>
            <li>Deliver and embed your video content and generate viewing analytics.</li>
            <li>Protect the platform against spam, fraud, and abuse.</li>
            <li>Respond to your requests and provide customer support.</li>
          </ul>
        </Section>

        <Section title="Service providers we share data with">
          <p>We share limited information with trusted providers only as needed to run the service:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><span className="text-gray-300">Cloud storage</span> — Cloudflare R2 stores your uploaded videos, recordings, and images.</li>
            <li><span className="text-gray-300">Streaming infrastructure</span> — our media server (datarhei Restreamer) ingests and delivers your live streams and VODs.</li>
            <li><span className="text-gray-300">Payments</span> — our payment processor handles card transactions and recurring billing.</li>
            <li><span className="text-gray-300">Email delivery</span> — a transactional email provider sends account and notification emails.</li>
            <li><span className="text-gray-300">Bot protection</span> — Cloudflare Turnstile helps verify that sign-ups are from real people.</li>
          </ul>
          <p>We do not sell your personal information.</p>
        </Section>

        <Section title="The Ascend TV App">
          <p>
            The Ascend TV App is a viewer-facing application (for TV and mobile devices) that displays a
            directory of church channels published through StreamWorship, along with each church's live
            stream, video library, and playlists.
          </p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><span className="text-gray-300">What it shows</span> — publicly available channel information such as church name, logo, brand color, live status, and the videos a church has chosen to publish.</li>
            <li><span className="text-gray-300">What it collects</span> — basic app and device information and viewing activity (for example, which channels or videos are played) used to operate the app and improve reliability.</li>
            <li><span className="text-gray-300">Accounts</span> — the Ascend TV App is designed for viewing and generally does not require a viewer login; it does not collect church admin passwords or billing details.</li>
          </ul>
          <p>
            Content displayed in the Ascend TV App is controlled by each church through their StreamWorship
            account, including which streams and videos are made available for viewing.
          </p>
        </Section>

        <Section title="Cookies and similar technologies">
          <p>
            We use strictly necessary cookies and local storage to keep you signed in and to remember
            preferences. Analytics may use identifiers to measure how content is viewed. You can control
            cookies through your browser settings, though some features may not work without them.
          </p>
        </Section>

        <Section title="Data retention">
          <p>
            We retain your account information and content for as long as your account is active. When you
            delete a video or recording it is removed from our storage; when you close your account we
            delete or anonymize your data within a reasonable period, except where we must retain it to
            meet legal, tax, or security obligations.
          </p>
        </Section>

        <Section title="Security">
          <p>
            We use industry-standard safeguards including encryption in transit, hashed passwords, and
            access controls. No method of transmission or storage is completely secure, but we work to
            protect your information and promptly address issues we discover.
          </p>
        </Section>

        <Section title="Your rights and choices">
          <p>
            Depending on where you live, you may have the right to access, correct, export, or delete your
            personal information, and to object to or restrict certain processing. To exercise these
            rights, contact us using the details below. You may also update most account details directly
            in your dashboard.
          </p>
        </Section>

        <Section title="Children's privacy">
          <p>
            StreamWorship is intended for organizations and adults. It is not directed to children under 13,
            and we do not knowingly collect personal information from them. Note that video content you
            publish may feature members of your congregation; you are responsible for obtaining any consents
            required for the content you broadcast.
          </p>
        </Section>

        <Section title="Changes to this policy">
          <p>
            We may update this Privacy Policy from time to time. When we make material changes we will
            update the "Last updated" date above and, where appropriate, notify you.
          </p>
        </Section>

        <Section title="Contact us">
          <p>
            If you have questions about this Privacy Policy or your data, contact us at{" "}
            <a href="mailto:privacy@streamworship.org" data-testid="privacy-contact-email" className="text-amber-400 hover:underline">privacy@streamworship.org</a>.
          </p>
        </Section>

        <div className="mt-12 border-t border-white/10 pt-6 text-xs text-gray-600">
          © 2026 StreamWorship · streamworship.org
        </div>
      </main>
    </div>
  );
}
